import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { useAuth, API } from "../App";
import axios from "axios";
import { 
  FileText, 
  Shield, 
  Home, 
  Users, 
  Map, 
  MessageSquare,
  ChevronRight,
  Mountain,
  ArrowDown,
  HelpCircle
} from "lucide-react";

const LandingPage = () => {
  const { user } = useAuth();
  
  // Site settings state
  const [siteSettings, setSiteSettings] = useState({
    site_name: "Horse Mountain Ranch",
    site_tagline: "Homeowners Association",
    logo_url: null,
    background_url: null,
  });
  
  // Fetch site settings on load
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API}/admin/site-settings`);
        setSiteSettings(response.data);
      } catch (error) {
        console.log("Using default site settings");
      }
    };
    fetchSettings();
  }, []);
  
  // Default background image
  const defaultBackground = "https://images.unsplash.com/photo-1685628538332-23bfcc4e7a08?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBtb3VudGFpbiUyMHJhbmNoJTIwaG9tZSUyMGV4dGVyaW9yfGVufDB8fHx8MTc2ODU0MjI1MXww&ixlib=rb-4.1.0&q=85";
  const backgroundImage = siteSettings.background_url || defaultBackground;

  const features = [
    {
      icon: <FileText className="w-6 h-6" />,
      title: "Document Library",
      description: "Access CC&Rs, Bylaws, Design Guidelines, and all HOA documents in one searchable location."
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Notices & Enforcement",
      description: "Track notices, receive updates, and maintain compliance with community standards."
    },
    {
      icon: <Home className="w-6 h-6" />,
      title: "Design Review",
      description: "Submit architectural requests and track DRC approval status online."
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Owner Portal",
      description: "View your property details, dues balance, and correspondence history."
    },
    {
      icon: <Map className="w-6 h-6" />,
      title: "Property Maps",
      description: "Interactive GIS maps showing lots, easements, and building envelopes."
    },
    {
      icon: <MessageSquare className="w-6 h-6" />,
      title: "AI Assistant",
      description: "Get instant answers about HOA rules with citations from governing documents."
    },
    {
      icon: <HelpCircle className="w-6 h-6" />,
      title: "Help Center",
      description: "Searchable knowledge base, FAQs, and AI-powered support for all your questions."
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <div 
        className="relative min-h-[90vh] flex flex-col"
        style={{
          backgroundImage: `linear-gradient(to bottom, rgba(26, 77, 46, 0.75), rgba(15, 28, 21, 0.9)), url('${backgroundImage}')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        {/* Navigation */}
        <nav className="w-full px-4 md:px-8 py-6 relative z-10">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              {siteSettings.logo_url ? (
                <img 
                  src={siteSettings.logo_url} 
                  alt={siteSettings.site_name} 
                  className="h-10 w-auto object-contain"
                />
              ) : (
                <Mountain className="w-8 h-8 text-copper" />
              )}
              <span className="font-serif text-xl text-sand font-semibold tracking-tight">
                {siteSettings.site_name}
              </span>
            </div>
            <div className="flex items-center gap-4">
              {user ? (
                <Link to={user.role === "owner" ? "/owner" : "/board"}>
                  <Button 
                    data-testid="dashboard-btn"
                    className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-button hover:shadow-button-hover transition-all duration-300"
                  >
                    Go to Dashboard
                  </Button>
                </Link>
              ) : (
                <>
                  <Link to="/login">
                    <Button 
                      variant="ghost" 
                      data-testid="login-btn"
                      className="text-sand hover:text-copper hover:bg-white/10 transition-colors duration-200"
                    >
                      Sign In
                    </Button>
                  </Link>
                  <Link to="/register">
                    <Button 
                      data-testid="register-btn"
                      className="bg-secondary text-secondary-foreground hover:bg-secondary/90 shadow-soft hover:shadow-elevated transition-all duration-300"
                    >
                      Get Started
                    </Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        </nav>

        {/* Hero Content */}
        <div className="flex-1 flex items-center px-4 md:px-8 relative z-10">
          <div className="max-w-7xl mx-auto w-full">
            <div className="max-w-2xl animate-fade-in-up">
              <p className="text-copper uppercase tracking-widest text-sm font-medium mb-4 opacity-0 animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
                {siteSettings.site_tagline || "HOA Management Platform"}
              </p>
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-sand font-bold leading-tight mb-6 opacity-0 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
                Your Community,<br />
                <span className="text-copper italic">Simplified.</span>
              </h1>
              <p className="text-sand/80 text-lg mb-8 leading-relaxed opacity-0 animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
                A comprehensive platform for {siteSettings.site_name} homeowners and board members. 
                Access documents, submit design requests, track notices, and stay connected 
                with your community.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 opacity-0 animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
                <Link to={user ? (user.role === "owner" ? "/owner" : "/board") : "/register"}>
                  <Button 
                    size="lg"
                    data-testid="hero-cta-btn"
                    className="bg-primary text-primary-foreground hover:bg-primary/90 h-12 px-8 text-base shadow-button hover:shadow-button-hover hover:-translate-y-0.5 transition-all duration-300"
                  >
                    {user ? "Go to Dashboard" : "Owner Portal"}
                    <ChevronRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
                <Link to="/login">
                  <Button 
                    size="lg"
                    variant="outline"
                    data-testid="board-login-btn"
                    className="border-sand/30 text-sand hover:bg-sand/10 h-12 px-8 text-base hover:border-sand/50 transition-all duration-300"
                  >
                    Board Member Login
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-8 h-12 border-2 border-sand/30 rounded-full flex items-center justify-center">
            <ArrowDown className="w-4 h-4 text-sand/50" />
          </div>
        </div>
      </div>

      {/* Features Section */}
      <section className="py-24 px-4 md:px-8 bg-background">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-secondary uppercase tracking-widest text-sm font-medium mb-4">
              Platform Features
            </p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-4">
              Everything You Need
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              A complete solution for managing your HOA community, from document storage 
              to AI-powered assistance.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <div 
                key={index}
                data-testid={`feature-card-${index}`}
                className="bg-card rounded-xl border border-border/50 p-8 hover:-translate-y-1 hover:shadow-floating hover:border-border transition-all duration-300 group"
              >
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center text-primary mb-6 group-hover:bg-primary/15 group-hover:scale-110 transition-all duration-300">
                  {feature.icon}
                </div>
                <h3 className="font-serif text-xl font-semibold text-card-foreground mb-3">
                  {feature.title}
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-4 md:px-8 bg-charcoal relative overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute top-0 left-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-secondary/5 rounded-full blur-3xl translate-x-1/2 translate-y-1/2" />
        
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-sand mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-sand/70 text-lg mb-8 max-w-2xl mx-auto leading-relaxed">
            Join your neighbors on the Horse Mountain Ranch HOA platform. 
            Access your property information, documents, and more.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register">
              <Button 
                size="lg"
                data-testid="cta-register-btn"
                className="bg-secondary text-secondary-foreground hover:bg-secondary/90 h-12 px-8 shadow-soft hover:shadow-elevated hover:-translate-y-0.5 transition-all duration-300"
              >
                Create Account
              </Button>
            </Link>
            <Link to="/login">
              <Button 
                size="lg"
                variant="outline"
                data-testid="cta-login-btn"
                className="border-sand/30 text-sand hover:bg-sand/10 h-12 px-8 hover:border-sand/50 transition-all duration-300"
              >
                Sign In
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 md:px-8 bg-charcoal-light border-t border-border/20">
        <div className="max-w-7xl mx-auto flex items-center justify-center">
          <div className="flex items-center gap-2">
            <Mountain className="w-5 h-5 text-copper" />
            <span className="text-sand/70 text-sm">
              © 2026 Horse Mountain Ranch HOA
            </span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
